import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useProfile, useInvestments } from "@/lib/data-hooks";
import { fmtMoney } from "@/lib/format";
import { Wallet, TrendingUp, TrendingDown, Trophy, PieChart as PieIcon, AlertCircle, RefreshCw, Inbox } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TRACKED = [
  { id: "bitcoin", symbol: "BTC", color: "#f7931a" },
  { id: "ethereum", symbol: "ETH", color: "#627eea" },
  { id: "solana", symbol: "SOL", color: "#14f195" },
  { id: "cardano", symbol: "ADA", color: "#0033ad" },
  { id: "binancecoin", symbol: "BNB", color: "#f3ba2f" },
  { id: "ripple", symbol: "XRP", color: "#23292f" },
  { id: "dogecoin", symbol: "DOGE", color: "#c2a633" },
  { id: "avalanche-2", symbol: "AVAX", color: "#e84142" },
] as const;

type Market = { id: string; current_price: number; price_change_percentage_24h: number };

function useTopMovers() {
  return useQuery({
    queryKey: ["cg-top", TRACKED.map((t) => t.id).join(",")],
    queryFn: async (): Promise<Market[]> => {
      const ids = TRACKED.map((t) => t.id).join(",");
      const r = await fetch(`https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&price_change_percentage=24h`);
      if (!r.ok) throw new Error("market");
      return r.json();
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

export function PortfolioInsights() {
  const profileQ = useProfile();
  const investmentsQ = useInvestments();
  const marketQ = useTopMovers();

  const profile = profileQ.data;
  const investments = investmentsQ.data ?? [];
  const market = marketQ.data ?? [];
  const accountLoading = profileQ.isLoading || investmentsQ.isLoading;
  const accountError = profileQ.error || investmentsQ.error;

  const balance = Number(profile?.balance ?? 0);
  const totalEarned = Number(profile?.total_earned ?? 0);
  const totalDeposited = Number(profile?.total_deposited ?? 0);

  const todayProfit = useMemo(() => {
    const since = new Date();
    since.setHours(0, 0, 0, 0);
    return investments
      .filter((i) => i.last_accrual_at && new Date(i.last_accrual_at) >= since)
      .reduce((s, i) => {
        const amt = Number(i.amount ?? 0);
        const roi = Number(i.plan?.daily_roi ?? 0);
        return s + (amt * roi) / 100;
      }, 0);
  }, [investments]);

  const planRoi = useMemo(() => {
    const map = new Map<string, { name: string; invested: number; earned: number; roi: number }>();
    for (const inv of investments) {
      const name = inv.plan?.name ?? "Plan";
      const cur = map.get(name) ?? { name, invested: 0, earned: 0, roi: 0 };
      cur.invested += Number(inv.amount ?? 0);
      cur.earned += Number(inv.earned ?? 0);
      cur.roi = cur.invested > 0 ? (cur.earned / cur.invested) * 100 : 0;
      map.set(name, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.roi - a.roi);
  }, [investments]);

  const topPerformers = useMemo(() => {
    return [...market]
      .map((m) => ({ ...m, meta: TRACKED.find((t) => t.id === m.id)! }))
      .sort((a, b) => (b.price_change_percentage_24h ?? 0) - (a.price_change_percentage_24h ?? 0))
      .slice(0, 5);
  }, [market]);

  const portfolioRoi = totalDeposited > 0 ? (totalEarned / totalDeposited) * 100 : 0;

  return (
    <div className="glass rounded-2xl p-5 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PieIcon className="h-4 w-4 text-primary" />
          <h2 className="text-base font-semibold">Portfolio Insights</h2>
        </div>
        <span className="text-[11px] text-muted-foreground">Live · refreshes every 2m</span>
      </div>

      {accountLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <TileSkeleton key={i} />)}
        </div>
      ) : accountError ? (
        <ErrorState message="Couldn't load your account stats." onRetry={() => { profileQ.refetch(); investmentsQ.refetch(); }} />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <InsightTile icon={Wallet} label="Total Holdings" value={fmtMoney(balance)} hint={`From ${investments.length} positions`} accent="primary" />
          <InsightTile
            icon={todayProfit >= 0 ? TrendingUp : TrendingDown}
            label="Today's Profit"
            value={`${todayProfit >= 0 ? "+" : ""}${fmtMoney(todayProfit)}`}
            hint="Accrued today"
            accent={todayProfit >= 0 ? "success" : "danger"}
          />
          <InsightTile icon={TrendingUp} label="Lifetime ROI" value={`${portfolioRoi.toFixed(2)}%`} hint={`${fmtMoney(totalEarned)} earned`} accent="gold" />
          <InsightTile icon={Trophy} label="Active Plans" value={String(investments.filter((i) => i.status === "active").length)} hint={`${planRoi.length} unique`} accent="primary" />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">ROI by Investment Plan</div>
          {investmentsQ.isLoading ? (
            <div className="space-y-2.5">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-lg bg-white/5 p-3 space-y-2">
                  <div className="flex justify-between"><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-14" /></div>
                  <Skeleton className="h-1.5 w-full" />
                  <div className="flex justify-between"><Skeleton className="h-3 w-20" /><Skeleton className="h-3 w-20" /></div>
                </div>
              ))}
            </div>
          ) : investmentsQ.error ? (
            <ErrorState message="Couldn't load investment plans." onRetry={() => investmentsQ.refetch()} />
          ) : planRoi.length === 0 ? (
            <EmptyState icon={Inbox} title="No active investment plans yet" hint="Start a plan to see ROI breakdowns here." />
          ) : (
            <div className="space-y-2.5">
              {planRoi.map((p) => {
                const pct = Math.min(100, Math.max(0, p.roi));
                return (
                  <div key={p.name} className="rounded-lg bg-white/5 p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium">{p.name}</span>
                      <span className={cn("font-mono text-sm", p.roi >= 0 ? "text-emerald-400" : "text-red-400")}>
                        {p.roi >= 0 ? "+" : ""}{p.roi.toFixed(2)}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-primary to-amber-400" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between text-[11px] text-muted-foreground font-mono mt-1.5">
                      <span>Invested {fmtMoney(p.invested)}</span>
                      <span>Earned {fmtMoney(p.earned)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Top Performer Assets · 24h</div>
          {marketQ.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg bg-white/5 p-2.5">
                  <Skeleton className="h-3 w-3" />
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-1.5"><Skeleton className="h-3.5 w-12" /><Skeleton className="h-3 w-16" /></div>
                  <Skeleton className="h-4 w-14" />
                </div>
              ))}
            </div>
          ) : marketQ.error ? (
            <ErrorState message="Couldn't reach market data." onRetry={() => marketQ.refetch()} />
          ) : topPerformers.length === 0 ? (
            <EmptyState icon={Inbox} title="No market data available" hint="Check back in a moment." />
          ) : (
            <div className="space-y-2">
              {topPerformers.map((a, idx) => (
                <div key={a.id} className="flex items-center gap-3 rounded-lg bg-white/5 p-2.5">
                  <span className="text-[11px] font-mono text-muted-foreground w-4">{idx + 1}</span>
                  <span className="h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: `${a.meta.color}22`, color: a.meta.color }}>
                    {a.meta.symbol}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{a.meta.symbol}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{fmtMoney(a.current_price, a.current_price < 1 ? 4 : 2)}</div>
                  </div>
                  <span className={cn("font-mono text-sm", a.price_change_percentage_24h >= 0 ? "text-emerald-400" : "text-red-400")}>
                    {a.price_change_percentage_24h >= 0 ? "▲" : "▼"} {Math.abs(a.price_change_percentage_24h ?? 0).toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InsightTile({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  hint: string;
  accent: "primary" | "success" | "danger" | "gold";
}) {
  const tone = {
    primary: "text-primary bg-primary/10",
    success: "text-emerald-400 bg-emerald-400/10",
    danger: "text-red-400 bg-red-400/10",
    gold: "text-amber-400 bg-amber-400/10",
  }[accent];
  return (
    <div className="rounded-xl bg-white/5 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className={cn("h-7 w-7 rounded-lg flex items-center justify-center", tone)}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <div className="font-mono text-lg font-semibold leading-tight">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{hint}</div>
    </div>
  );
}

function TileSkeleton() {
  return (
    <div className="rounded-xl bg-white/5 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Skeleton className="h-7 w-7 rounded-lg" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-5 w-24 mb-1.5" />
      <Skeleton className="h-3 w-16" />
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-4 flex items-start gap-3">
      <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-red-200">{message}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">Please try again in a moment.</div>
      </div>
      <Button size="sm" variant="ghost" onClick={onRetry} className="h-7 gap-1.5 text-xs">
        <RefreshCw className="h-3 w-3" /> Retry
      </Button>
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint }: { icon: React.ElementType; title: string; hint: string }) {
  return (
    <div className="rounded-lg bg-white/5 py-8 px-4 text-center">
      <Icon className="h-6 w-6 text-muted-foreground mx-auto mb-2 opacity-60" />
      <div className="text-sm font-medium">{title}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}